import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { APP } from '@/config/app';
import { useCmsDocument } from '@/hooks/useCmsDocument';
import {
  FOOTER_LEGAL_ROUTES,
  NAV_ROUTES,
  type FooterLegalKey,
  type NavKey,
} from '@/lib/cms/nav-routes';
import { findSection } from '@/lib/cms/section-types';
import { openConsentPreferences } from '@/lib/cookie-consent';
import { getWhatsAppHref } from '@/lib/whatsapp';
import './Footer.css';

type FooterLink<K extends string> = { id: string; key: K; label: string };

// Site-wide footer mounted on PublicLayout and BuyerLayout. Four sections
// on desktop (brand, explore, legal, contact + follow), single column on
// mobile. The "Follow" links only render when APP.social.* is non-null,
// so the footer stays clean while social accounts are still placeholders.
//
// Copy is CMS-driven (`footer` slug) and strict: while the document loads,
// when a section is absent or hidden, or for items hidden / left without a
// label, the element is simply not rendered — no i18n fallback resurrects
// content. Structural headings and functional items (cookie-preferences
// button, WhatsApp, social links) carry behaviour, not copy — they stay
// outside the CMS.
export function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const hasInstagram = Boolean(APP.social.instagram);
  const hasLinkedin = Boolean(APP.social.linkedin);
  const hasFollow = hasInstagram || hasLinkedin;

  const footerDoc = useCmsDocument('footer');

  const exploreSection = findSection(footerDoc, 'nav_links');
  const exploreLinks: FooterLink<NavKey>[] =
    exploreSection && exploreSection.visible
      ? exploreSection.items
          .filter((item) => item.visible && item.label.trim() !== '')
          .map((item) => ({ id: item.id, key: item.key, label: item.label }))
      : [];

  const legalSection = findSection(footerDoc, 'footer_legal');
  const legalLinks: FooterLink<FooterLegalKey>[] =
    legalSection && legalSection.visible
      ? legalSection.items
          .filter((item) => item.visible && item.label.trim() !== '')
          .map((item) => ({ id: item.id, key: item.key, label: item.label }))
      : [];

  const metaSection = findSection(footerDoc, 'footer_meta');
  const meta = metaSection && metaSection.visible ? metaSection : null;
  const tagline = meta?.tagline.trim() ? meta.tagline : null;
  // The CMS copy keeps a literal {{year}} token, interpolated at render time.
  const copyright = meta?.copyright.trim()
    ? meta.copyright.replaceAll('{{year}}', String(year))
    : null;

  return (
    <footer className="site-footer" role="contentinfo">
      <div className="container site-footer__inner">
        <div className="site-footer__sections">
          <div className="site-footer__section site-footer__brand">
            <Link to="/" className="site-footer__brand-link" aria-label={APP.name}>
              <img src="/logo.svg" alt="" className="site-footer__brand-logo" />
            </Link>
            {tagline && <p className="site-footer__tagline">{tagline}</p>}
          </div>

          <nav className="site-footer__section" aria-label={t('footer.sections.explore')}>
            <h2 className="site-footer__heading">{t('footer.sections.explore')}</h2>
            <ul className="site-footer__list">
              {exploreLinks.map((link) => (
                <li key={link.id}>
                  <Link to={NAV_ROUTES[link.key]}>{link.label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-footer__section">
            <h2 className="site-footer__heading">{t('footer.sections.legal')}</h2>
            <ul className="site-footer__list">
              {legalLinks.map((link) => (
                <li key={link.id}>
                  <Link to={FOOTER_LEGAL_ROUTES[link.key]}>{link.label}</Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  className="site-footer__link-btn"
                  onClick={openConsentPreferences}
                >
                  {t('footer.cookiePreferences')}
                </button>
              </li>
            </ul>
          </div>

          <div className="site-footer__section">
            <h2 className="site-footer__heading">{t('footer.sections.contact')}</h2>
            <ul className="site-footer__list">
              <li>
                <a
                  href={getWhatsAppHref()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="site-footer__contact-link"
                >
                  {t('footer.whatsapp')}
                </a>
              </li>
            </ul>
            {hasFollow ? (
              <>
                <h2 className="site-footer__heading site-footer__heading--follow">
                  {t('footer.sections.follow')}
                </h2>
                <ul className="site-footer__list">
                  {hasInstagram ? (
                    <li>
                      <a href={APP.social.instagram!} target="_blank" rel="noopener noreferrer">
                        {t('footer.instagram')}
                      </a>
                    </li>
                  ) : null}
                  {hasLinkedin ? (
                    <li>
                      <a href={APP.social.linkedin!} target="_blank" rel="noopener noreferrer">
                        {t('footer.linkedin')}
                      </a>
                    </li>
                  ) : null}
                </ul>
              </>
            ) : null}
          </div>
        </div>

        <div className="site-footer__bottom">
          {copyright && <p className="site-footer__copyright">{copyright}</p>}
          <p className="site-footer__credit">
            {t('footer.createdBy')}{' '}
            <a href="https://previsualiser.com" target="_blank" rel="noopener noreferrer">
              Previsualiser
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
